import type { PresenceUser } from '@mentimeter/editor-realtime';
import {
  getActivePresenters,
  getEditorDefaultPresenceData,
} from '@mentimeter/editor-realtime';
import { Avatar, AVATAR_SIZE_MAP, AvatarList } from '@mentimeter/editor-ui';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Clickable } from '@mentimeter/ragnar-ui/clickable';
import {
  PopoverBody,
  PopoverContainer,
  PopoverRoot,
  PopoverTrigger,
} from '@mentimeter/ragnar-ui/popover';
import { getNameInitials, type UserT } from '@mentimeter/user';
import * as React from 'react';
import { useId, useState } from 'react';
import { EditorUserAvatar } from './EditorUserAvatar';

export interface PresenceAvatarsProps {
  onAvatarClick?: ((avatar?: string) => void) | undefined;
  user: UserT | null;
  presenceUsers: PresenceUser[];
  children?: React.ReactNode | undefined;
  avatarSize?: 1 | 2 | 3 | 4 | undefined;
}

const AVATAR_SIZE = 3;
const MAX_AVATARS_VISIBLE = 4;
const MAX_AVATARS_IN_LIST = 10;
export const AVATAR_PERCENTAGE = 0.75;

export const PresenceAvatars = ({
  onAvatarClick,
  user,
  presenceUsers,
  children,
  avatarSize = AVATAR_SIZE,
}: PresenceAvatarsProps) => {
  const uid = useId();
  if (!user) return null;

  const allPresenceUsers = [
    getEditorDefaultPresenceData(user),
    ...presenceUsers,
  ];

  const uniquePresenceUsers = Array.from(
    allPresenceUsers
      .reduce((acc, current) => {
        if (current.user_id) {
          const existingUser = acc.get(current.user_id);
          if (existingUser && existingUser.actor === 'presenter') {
            return acc;
          }
          acc.set(current.user_id, current);
        }
        return acc;
      }, new Map<number, PresenceUser>())
      .values(),
  );

  const activePresenters = getActivePresenters(uniquePresenceUsers);
  const { visiblePresenceUsers, hiddenPresenceUsers } =
    getVisibleAndHiddenPresenceUsers(uniquePresenceUsers);

  return (
    <Box
      alignItems="center"
      flexDirection="row"
      pr={`calc(${AVATAR_SIZE_MAP[avatarSize].width} * ${
        1 - AVATAR_PERCENTAGE
      })`}
    >
      <Box flexDirection="row">
        {visiblePresenceUsers.map((c) => {
          return (
            <EditorUserAvatar
              id={`${uid}-${c.device_id}`}
              key={c.device_id}
              presenceUser={c}
              isPresenting={activePresenters.includes(c)}
              onClick={onAvatarClick}
              avatarSize={avatarSize}
            />
          );
        })}
        {hiddenPresenceUsers.length > 0 && (
          <HiddenPresenceUsers
            id={uid}
            hiddenPresenceUsers={hiddenPresenceUsers}
            activePresenters={activePresenters}
            onClick={onAvatarClick}
            avatarSize={avatarSize}
          />
        )}
        {children}
      </Box>
    </Box>
  );
};

function getVisibleAndHiddenPresenceUsers(presenceUsers: PresenceUser[]) {
  if (presenceUsers.length > MAX_AVATARS_VISIBLE) {
    return {
      visiblePresenceUsers: presenceUsers.slice(0, MAX_AVATARS_VISIBLE - 1),
      hiddenPresenceUsers: presenceUsers.slice(MAX_AVATARS_VISIBLE - 1),
    };
  }

  return {
    visiblePresenceUsers: presenceUsers,
    hiddenPresenceUsers: [],
  };
}

function HiddenPresenceUsers({
  id,
  hiddenPresenceUsers,
  activePresenters,
  onClick,
  avatarSize,
}: {
  id: string;
  hiddenPresenceUsers: PresenceUser[];
  activePresenters: PresenceUser[];
  onClick?: ((avatar?: string | undefined) => void) | undefined;
  avatarSize: 1 | 2 | 3 | 4;
}) {
  const [showList, setShowList] = useState(false);

  return (
    <Box
      extend={() => ({
        width: `calc(${AVATAR_SIZE_MAP[AVATAR_SIZE].width} * .75)`,
      })}
    >
      <PopoverRoot open={showList} onOpenChange={setShowList}>
        <PopoverTrigger>
          <Clickable
            extend={() => ({
              '@media (hover: hover)': { ':hover': { opacity: 1 } },
            })}
          >
            <Avatar
              id={`${id}-list`}
              size={avatarSize}
              initials={`${hiddenPresenceUsers.length}`}
              name=""
              bg="neutralWeak"
              onAvatarClick={() => setShowList(!showList)}
            />
          </Clickable>
        </PopoverTrigger>
        <PopoverContainer>
          <PopoverBody>
            <AvatarList
              collaborators={hiddenPresenceUsers}
              maxAvatarsInList={MAX_AVATARS_IN_LIST}
              onAvatarClick={onClick}
              activePresenters={activePresenters}
              onGetNameInitials={getNameInitials}
            />
          </PopoverBody>
        </PopoverContainer>
      </PopoverRoot>
    </Box>
  );
}
